import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Role } from '@ndvr/auth-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useInternalUserProfilesQuery } from '@/modules/internal-user-queries';
import { useUserHasRole } from 'views/admin/dev-queries';
import { PageSpinner } from 'widgets';

import {
    type InternalUserProfileRequest,
    useDeleteInternalUserProfileMutation,
    useUpdateInternalUserProfileMutation,
} from './internal-user-profile-queries';
import { InternalUserProfileForm } from './InternalUserProfileForm';
import { RoleManagement } from './RoleManagement';
import { TenantPermissionsCard } from './TenantPermissionsCard';

export const InternalUserProfileDetails = ({ profileGUID }: { profileGUID: string }) => {
    const navigate = useNavigate();
    const { data: internalUsers = [], isLoading, isError, refetch } = useInternalUserProfilesQuery();
    const updateProfileMutation = useUpdateInternalUserProfileMutation();
    const deleteProfileMutation = useDeleteInternalUserProfileMutation();
    const { data: hasRoleAdmin = false } = useUserHasRole(Role.ROLE_ADMIN);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const profile = useMemo(() => {
        return internalUsers.find((item) => item.profileGUID === profileGUID && !item.archived);
    }, [internalUsers, profileGUID]);

    const handleSaveProfile = (profileUpdate: InternalUserProfileRequest) => {
        if (!profile) {
            return;
        }

        updateProfileMutation.mutate({
            profileGUID: profile.profileGUID,
            profile: profileUpdate,
        });
    };

    const handleConfirmDelete = () => {
        if (!profile) {
            return;
        }

        deleteProfileMutation.mutate(profile.profileGUID, {
            onSuccess: () => {
                navigate('/admin/internal-users');
            },
            onSettled: () => setDeleteConfirmOpen(false),
        });
    };

    if (isLoading) {
        return <PageSpinner />;
    }

    if (isError) {
        return (
            <div className="text-destructive space-y-3">
                <div>Failed to load internal users.</div>
                <Button variant="outline" onClick={() => refetch()}>
                    Retry
                </Button>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="space-y-3">
                <div className="text-muted-foreground">Internal user profile not found.</div>
            </div>
        );
    }

    const profileName = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() || profile.email;

    return (
        <div className="space-y-6">
            <Card>
                <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
                    <CardTitle className="font-heading font-medium">{profileName}</CardTitle>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="text-muted-foreground text-xs uppercase">PersonOrgGUID</div>
                            <CopiableGuid guid={profile.profileGUID ?? ''} showBorder={false} />
                        </div>
                        <Button variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                            Archive User
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="flex h-full flex-col">
                    <CardHeader className="pb-0">
                        <CardTitle className="font-heading font-medium">Profile Details</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-1 flex-col gap-6">
                        <InternalUserProfileForm
                            profile={profile}
                            isPending={updateProfileMutation.isPending}
                            onSubmit={handleSaveProfile}
                        />
                    </CardContent>
                </Card>

                {hasRoleAdmin && (
                    <Card>
                        <CardHeader className="pb-0">
                            <CardTitle className="font-heading font-medium">Edit Roles</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <RoleManagement userGUID={profile.profileGUID} />
                        </CardContent>
                    </Card>
                )}
            </div>

            {hasRoleAdmin && <TenantPermissionsCard internalUserGUID={profile.profileGUID} />}

            <Dialog open={deleteConfirmOpen} onOpenChange={(open) => !open && setDeleteConfirmOpen(false)}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Archive user</DialogTitle>
                        <DialogDescription>
                            {`This will archive ${profile.email}. This action cannot be undone.`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmOpen(false)}
                            disabled={deleteProfileMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteProfileMutation.isPending}
                        >
                            {deleteProfileMutation.isPending ? 'Archiving...' : 'Archive'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
